import {Keypoints, PointName, predictedHeight} from '@kemtai/keypoints';
import {SideT, TugState} from './types';

const body:  PointName[] = ['left_shoulder', 'right_shoulder', 'left_hip', 'right_hip'] 
const front: PointName[] = ['front_1', 'front_2', 'front_3', 'front_4', 'front_5', 'neck'] 
const back:  PointName[] = ['back_1', 'back_2', 'back_3', 'back_4', 'back_5', 'neck_back']

function degrees(radians: number): number {
    return radians * 180 / Math.PI;
}

function abs(x: number|null):  number|null {
    if (x === null) {
        return null
    }
    return Math.abs(x)
}

function allPresent(kp: Keypoints, points: PointName[]): boolean {
    return points.every(k => kp[k])
}
function allAbsent(kp: Keypoints, points: PointName[]): boolean {
    return !points.some(k => kp[k])
}

function isFrontalView(kp: Keypoints):  boolean {
  return allPresent(kp, body) && allPresent(kp, front) &&  allAbsent(kp, back);
}

function isBackView(kp: Keypoints):  boolean {
  return allPresent(kp, body) && allPresent(kp, back) && allAbsent(kp, front);
}

function isProfileView(kp: Keypoints):  boolean {
    if (!allPresent(kp, body)) {
        return false
    }
    if (!allPresent(kp, back)) {
        return false
    }
    if (!allPresent(kp, front)) {
        return false
    }

    const h = predictedHeight(kp)
    if (h === null) {
        return false
    }
    const d = kp.dist('left_shoulder', 'right_shoulder')
    if (d === null) {
        return false
    }

    if (d / h * 175 > 30) {
        return false
    }
    return true
}

function getViewType(kp: Keypoints): string|null {
    if (isFrontalView(kp)) {
        return 'Frontal'
    }
    if (isBackView(kp)) {
        return 'Back'
    }
    if (isProfileView(kp)) {
        return 'Profile'
    }
    return null
} 

function meanX(kp: Keypoints, points: PointName[]): number|null {
    let sum = 0
    let count = 0
    for (let p of points) {
        if (kp[p]) {
            sum += kp[p]!.x
            count += 1
        }
    }
    if (count === 0) {
        return null
    }
    return sum / count
}

function profileSide(kp: Keypoints):  SideT|null {
    const frontX = meanX(kp, front)
    const backX = meanX(kp, back)
    if (frontX === null || backX === null) {
        return null
    }
    if (frontX < backX) {
        return 'left'
    } else {
        return 'right'
    }
}



function hAngle(kp: Keypoints, point1: PointName, point2: PointName): number|null {
    const p1 = kp[point1] 
    const p2 = kp[point2] 
    if (!p1 || !p2) {
        return null
    }
    return degrees(Math.atan2(p1.y - p2.y, p1.x - p2.x))
}

type StateT = "waiting"|"measuring"|null|"done";

export class SitToStandLogic {
    viewType: string|null = null;
    userPosition: string|null = null;
    state:    StateT = null;
    standUpTime: number|null = null;
    duration: number|null = null;   
    result: number|null = null;
    popupMessage: string|null = null;


    reset() {
        this.viewType = null;
        this.state = null;
        this.standUpTime = null;
        this.duration = null;   
        this.result = null;
        //this.popupMessage = null;
    }

    tugState(): TugState {

        let popupMessage : string = "";

        if (this.state === null) {
            popupMessage = "Please position yourself in front of the camera";
        } else if (this.state === 'waiting') {
            popupMessage = `Please stand up and walk 3 meters, turn, walk back and sit down.`;
        } else if (this.state === 'measuring' && this.duration! > 1000) {
            popupMessage = `Measuring...  ${(this.duration!/1000).toFixed(1)} seconds elapsed.`;
        }

        return {
            viewType: this.viewType, 
            userPosition: this.userPosition,
            measuring : this.state === 'measuring',
            duration: this.duration,
            result: this.result,
            state : this.state,
            popupMessage: popupMessage
        }
    }

    processFrame(kp: Keypoints): TugState {

        this.viewType = getViewType(kp);
        this.userPosition = null;
        if (this.viewType === 'Profile') {
            const _profileSide = profileSide(kp);
            if (_profileSide) {
                let angle = abs(hAngle(kp, `${_profileSide}_hip`, `${_profileSide}_knee`))
                if (angle !== null) {
                    if (angle < 0) {
                        angle = -angle
                    }
                    if (angle > 90) {
                        angle = 180 - angle
                    }
                    if (angle > 45) {
                        this.userPosition = 'Standing'
                    } else {
                        this.userPosition = 'Sitting'
                    }
                }
            }
        }



        if (this.result !== null) {
            return this.tugState();
        }

        if (this.userPosition === null) {
            if (this.state === 'waiting') {
                this.state = null;
            }

        } else if (this.userPosition === 'Standing') {
            if (this.state === 'waiting') {
                // user just stood up, start measuring
                this.state = 'measuring';
                this.result = null;
                this.standUpTime = Date.now();
            }
        } else if (this.userPosition === 'Sitting') {
            if (this.state === 'measuring') {
                // user just sat down, stop measuring
                this.result = Date.now() - this.standUpTime!;
                if (this.result > 1000) {
                    this.state = 'done';
                } else {
                    this.state = 'waiting';
                    this.result = null;
                }
            } else {
                this.state = 'waiting';
            }
        }

        if (this.state === 'measuring') {
            // update duration
            this.duration = Date.now() - this.standUpTime!;
        }

        return  this.tugState();
    }
}
